import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { UserProvider } from "./contexts/UserContext";
import { FilterProvider } from "./contexts/FilterContext";
import { NavigationProvider } from "./contexts/NavigationContext";
import { CommentProvider } from "./contexts/CommentContext";
import SecureRoute from "./components/SecureRoute";
import Login from "./components/Login";
import "./App.css";
import { Security, LoginCallback } from "@okta/okta-react";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { oktaConfig } from "./config/oktaConfig";
import { useAuthToken } from "./hooks/useAuthToken";
// Lazy load components
const ListingPage = lazy(() => import("./pages/ListingPage"));
const DetailPage = lazy(() => import("./pages/DetailPage"));
const ComparisonPage = lazy(() => import("./pages/ComparisonPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const HealthPage = lazy(() => import("./pages/HealthPage"));
// Configure Okta
const oktaAuth = new OktaAuth(oktaConfig);


// Create loading component
const Loader = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
    }}
  >
    Loading...
  </div>
);

// App wrapper to access router hooks
const AppWithRouting = () => {
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
    window.location.replace(toRelativeUrl(originalUri, window.location.origin));
  };
  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <FluentProvider
        theme={webLightTheme}
        style={{
          backgroundColor: "var(--colorNeutralBackground2)",
          minHeight: "100vh",
        }}
      >
        <link
          href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;600;700&family=Noto+Serif:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />

        <UserProvider>
          <FilterProvider>
            <CommentProvider>
              <TokenHandler />
              <Suspense fallback={<Loader />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/login/callback" element={<LoginCallback />} />
                  
                  {/* Protected routes */}
                  <Route path="/" element={
                    <SecureRoute>
                      <ListingPage />
                    </SecureRoute>
                  } />
                  <Route path="/detail/:id" element={
                    <SecureRoute>
                      <DetailPage />
                    </SecureRoute>
                  } />
                  <Route path="/detail/" element={
                    <SecureRoute>
                      <DetailPage />
                    </SecureRoute>
                  } />
                  <Route path="/compare/:id1/:id2" element={
                    <SecureRoute>
                      <ComparisonPage />
                    </SecureRoute>
                  } />
                  <Route path="/profile" element={
                    <SecureRoute>
                      <ProfilePage />
                    </SecureRoute>
                  } />
                  <Route path="/admin/health" element={
                    <SecureRoute>
                      <HealthPage />
                    </SecureRoute>
                  } />
                </Routes>
              </Suspense>
            </CommentProvider>
          </FilterProvider>
        </UserProvider>
      </FluentProvider>
    </Security>
  );
};

// Main App component
const App = () => {
  return (
    <BrowserRouter>
      <NavigationProvider>
        <AppWithRouting />
      </NavigationProvider>
    </BrowserRouter>
  );
};

// Component to handle token management
const TokenHandler = () => {
  useAuthToken();
  return null;
};

export default App;
